import React, { Fragment, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import styles from '@/pages/entry-lists/styles.less';
import {
  useCurrentDecedentId,
  useCurrentDecedentSections,
} from '@/app/providers/DecedentProvider';
import { routes } from '@/app/routing/index';
import { useLocalizedField } from '@/common/hooks/index';
import { useTranslation } from 'react-i18next';
import { useFilledDataEntriesQuery } from '@/entities/question';
import { useGeneralMetaDataQuery } from '@/entities/meta-data';

const EntryLists = () => {
  const { t } = useTranslation();
  const { data: metaData = {} } = useGeneralMetaDataQuery();
  const { general } = metaData;
  const getLocalizedField = useLocalizedField();
  const categoryMapper =
    getLocalizedField(general, 'dataEntryCategoryMapper');
  const decedentId = useCurrentDecedentId();
  const decedentSections = useCurrentDecedentSections();
  const { data: rawList = [] } = useFilledDataEntriesQuery(decedentId);
  const modules = useMemo(
    () => ({
      immediate: t('Immediate Tasks Module'),
      legal: t('Legal Tasks Module'),
      main: t('Main Tasks Module'),
      asset: t('Assets Tasks Module'),
    }),
    [t]
  );

  const list = useMemo(() => {
    const que = Object.keys(modules).reduce((acc, label, index) => {
      acc[label] = { label, position: index + 1 };
      return acc;
    }, {});

    return rawList
      .filter(({ section }) => modules[section])
      .sort((a, b) => que[a.section].position - que[b.section].position);
  }, [modules, rawList]);

  return (
    <div className={styles.container}>
      <h4 className={styles.title}>{t('Data entry lists')}</h4>
      <p className={styles.label}>
        {t(
          'On this page, you have fast access to the data you’ve entered in each module. To see the links the particular questionnaire should be finished (saving progress in the questionnaire doesn’t generate the links on this page).'
        )}
      </p>
      <div className={styles.list}>
        <Grid spacing={3} container>
          {list.map(({ section, dataEntries }) => {
            const { passed } = decedentSections[section].question;
            return (
              <Grid key={section} size={3}>
                <div className={styles.listItem}>
                  <div className={styles.listItemHeader}>
                    {modules[section]}
                  </div>
                  <div className={styles.listItemBody}>
                    {passed &&
                      dataEntries.map(({ category, list }, ind) => {
                        return (
                          <Fragment key={ind}>
                            {category && (
                              <span className={styles.listItemBodyItem}>
                                {categoryMapper[category]}
                              </span>
                            )}
                            {list.map((item) => (
                              <Link
                                key={item.slug}
                                className={cx(
                                  styles.listItemBodyItem,
                                  category && styles['listItemBodyItem-child']
                                )}
                                to={`${routes.dashboard.editQuestions}?module=${section}&slug=${item.slug}&backPage=entryLists`}
                              >
                                {getLocalizedField(item, 'name')}
                              </Link>
                            ))}
                          </Fragment>
                        );
                      })}
                    {!passed && (
                      <p className={styles.listItemBodyNoText}>
                        {t('Links will appear after questionnaire is finished')}
                      </p>
                    )}
                  </div>
                </div>
              </Grid>
            );
          })}
        </Grid>
      </div>
    </div>
  );
};

export default EntryLists;
